import { Injectable } from '@angular/core';
import { environment } from '@environments/environment.dev';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import * as moment from 'moment-timezone';

import { AppConstants } from '@app/app.constant';
import { ROUTE_PATH } from '@app/shared/enum/common.enum';
import { ICourses } from '@app/shared/model/program.model';
import { LOCAL_STORAGE_KEY, StorageService } from '@services/storage.service';

@Injectable({
    providedIn: 'root',
})
export class CommonStateService {
    public API_URL = `${environment.apiEndpoint}`;
    public programName = new BehaviorSubject<string>('');
    public programName$ = this.programName.asObservable();
    private readonly designationSubject = new BehaviorSubject<{
        designation: string | null;
        name: string | null;
    }>({ designation: null, name: null });
    public designation$ = this.designationSubject.asObservable();
    private readonly ekcRoutePath = new BehaviorSubject<string>(
        ROUTE_PATH.USE_CASES
    );
    public subdomain: string = '';
    public isDomain: boolean = false;
    public ekcRoutePath$ = this.ekcRoutePath.asObservable();
    private readonly structureNameSource = new BehaviorSubject<string>(''); // Default value
    structureName$ = this.structureNameSource.asObservable();

    private readonly treeStructureBuild = new BehaviorSubject<ICourses[]>([]);
    treeStructureBuild$ = this.treeStructureBuild.asObservable();
    private readonly popupState = new BehaviorSubject<boolean>(false);
    private readonly overviewPrograms = new BehaviorSubject<
        {
            value: number;
            name: string;
        }[]
    >([]);

    popupState$ = this.popupState.asObservable();
    private readonly commentCountSubject = new BehaviorSubject<boolean>(false);
    commentCount$ = this.commentCountSubject.asObservable();

    private readonly segmentFilesSubject = new BehaviorSubject<{
        [key: number]: any[];
    }>({});
    segmentFiles$ = this.segmentFilesSubject.asObservable();

    constructor(private readonly storageService: StorageService) {
        this.extractSubdomain();
    }
    /**
     * segmentFiles is an object with key as segmentId and value as array of files
     * @returns {[key: string]: any[]}
     */
    getSegmentFiles(): { [key: string]: any[] } {
        return this.segmentFilesSubject.getValue();
    }

    /**
     * updates the files for a segment
     * @param segmentId
     * @param files
     */
    updateFiles(segmentId: number, files: any[]) {
        const current = this.segmentFilesSubject.value;
        current[segmentId] = files;
        this.segmentFilesSubject.next({ ...current });
    }

    /**
     * updates the comment count
     * @param value
     */
    setCommentCount(value: boolean): void {
        this.commentCountSubject.next(value);
    }

    /**
     * returns the comment count
     */
    getCommentCount() {
        return this.commentCountSubject.getValue();
    }

    /**
     * show the popup
     */
    showPopup() {
        this.popupState.next(true);
    }

    /**
     * hide the popup
     */
    hidePopup() {
        this.popupState.next(false);
    }

    /**
     * returns the overview programs
     */
    getOverviewPrograms() {
        return this.overviewPrograms.getValue();
    }

    /**
     * updates the overview programs
     * @param data
     */
    setOverviewPrograms(
        data: {
            value: number;
            name: string;
        }[]
    ) {
        this.overviewPrograms.next(data);
    }

    /**
     * updates the profile data
     * @param designation
     * @param name
     */
    getProfileData(designation: string | null, name: string): void {
        this.designationSubject.next({ designation, name });
    }

    /**
     * sets the container name
     * @param name
     */
    setStructureName(name: string) {
        this.structureNameSource.next(name);
    }

    /**
     * updates the tree structure (container)
     * @param value
     */
    updateTreeStructureBuild(value: ICourses[]) {
        this.treeStructureBuild.next(value);
    }

    /**
     * returns the tree structure (container)
     */
    getTreeStructureBuild() {
        return this.treeStructureBuild.getValue();
    }

    /**
     * extracts the subdomain for organization
     */
    public extractSubdomain(): void {
        const hostname = window.location.hostname;
        const parts = hostname.split('.');

        if (parts.length > 0) {
            this.isDomain = true;
            this.subdomain = parts[0];
            if (this.subdomain === 'localhost') {
                this.isDomain = false;
            }
        } else {
            this.isDomain = false;
        }
    }

    /**
     * returns the subdomain
     */
    getSubdomain(): string {
        return this.subdomain;
    }

    /**
     * returns the program name
     */
    getProgramName(): string {
        return this.programName.getValue();
    }

    /**
     * returns the isDomain
     */
    getIsDomain(): boolean {
        return this.isDomain;
    }

    /**
     * sets the program name
     * @param name
     */
    setProgramName(name: string): void {
        this.programName.next(name);
    }

    /**
     * sets the ekc route path
     * @param data
     */
    setEkcRoutePath(data: string): void {
        this.ekcRoutePath.next(data);
    }

    /**
     * returns the ekc route path
     */
    getEkcRoutePath(): string {
        return this.ekcRoutePath.getValue();
    }

    /**
     * password strength validator
     * @param control
     */
    passwordStrengthValidator(control: AbstractControl) {
        const password = control.value;
        const passwordRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()])\S{8,}$/;

        if (password) {
            if (!/[a-z]/.test(password)) {
                return { passwordNoLowerCase: true };
            }
            if (!/[A-Z]/.test(password)) {
                return { passwordNoUpperCase: true };
            }
            if (!/\d/.test(password)) {
                return { passwordNoNumber: true };
            }
            if (!/[!@#$%^&*()]/.test(password)) {
                return { passwordNoSpecialChar: true };
            }
            if (/\s/.test(password)) {
                return { passwordHasWhitespace: true };
            }
            if (
                password.length <
                AppConstants.staticValidations.maxLength.password[0]
            ) {
                return { passwordTooShort: true };
            }
            if (!passwordRegex.test(password)) {
                return { weakPassword: true };
            }
        }
        return null;
    }

    /**
     * allows only numbers validator
     * @param event
     */
    allowOnlyNumbers(event: KeyboardEvent): void {
        const allowedKeys = [
            'Backspace',
            'ArrowLeft',
            'ArrowRight',
            'Tab',
            '+',
            ' ',
        ];
        const isDigit = /^[0-9]$/.test(event.key);
        const isAllowedKey = allowedKeys.includes(event.key);

        if (!isDigit && !isAllowedKey) {
            event.preventDefault();
        }
    }

    /**
     * no blank spaces validator
     */
    noBlankSpacesValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (value && typeof value === 'string' && value.trim() === '') {
                return { noBlankSpaces: true };
            }
            return null;
        };
    }

    /**
     * formats the date by language
     * @param utcDate
     * @param withTime
     */
    formatDateByLanguage(utcDate: string, withTime: boolean = true) {
        const clientTimezone = moment.tz.guess();

        const language_id = this.storageService.getItem(
            LOCAL_STORAGE_KEY.SELECTED_LANGUAGE
        ) as '1' | '2';

        const multiLingual =
            AppConstants.multiLingualDateFormat[
                withTime ? 'withTime' : 'withoutTime'
            ];

        const format = multiLingual[language_id] || multiLingual['1'];

        return moment.utc(utcDate).tz(clientTimezone).format(format);
    }
}
